import { useState } from "react"
import { Helmet } from "react-helmet-async"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { registerUser } from "../services/userService"
import { acceptInvitation } from "../services/chaperonInvitationsConnector"
import { getRecaptchaToken } from "../utilities/recaptcha"
import {
    validateEmail,
    validatePassword,
    validateUsername,
} from "../utilities/validation"
import "../css/user-registration.css"

export default function UserRegister() {
    const navigate = useNavigate()
    const location = useLocation()
    const invitationToken = location.state?.invitationToken || null
    // Where to go after signing up, e.g. the Analysis page a logged-out
    // visitor came from (see SharePreviewCard). Same state shape as login.
    const from = location.state?.from?.pathname || "/community"
    const invitationCommunityName = location.state?.communityName || null
    const [formData, setFormData] = useState({
        email: "",
        username: "",
        password: "",
        confirmPassword: "",
    })
    const [errors, setErrors] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitSuccess, setSubmitSuccess] = useState(false)

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }))

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: "",
            }))
        }
    }

    const validateForm = () => {
        const newErrors = {}

        if (!formData.email.trim()) {
            newErrors.email = "Email is required"
        } else if (!validateEmail(formData.email)) {
            newErrors.email = "Please enter a valid email address"
        }

        if (!formData.username.trim()) {
            newErrors.username = "Username is required"
        } else if (!validateUsername(formData.username)) {
            newErrors.username =
                "Username must be 3-50 characters and contain only letters, numbers, and underscores"
        }

        if (!formData.password) {
            newErrors.password = "Password is required"
        } else if (!validatePassword(formData.password)) {
            newErrors.password = "Password must be at least 8 characters long"
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = "Please confirm your password"
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = "Passwords do not match"
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (!validateForm()) {
            return
        }

        setIsSubmitting(true)
        setErrors({})

        try {
            const recaptchaToken = await getRecaptchaToken("register")
            const registrationData = {
                email: formData.email.trim(),
                username: formData.username.trim(),
                password: formData.password,
                recaptcha_token: recaptchaToken || undefined,
            }

            const response = await registerUser(registrationData)

            if (response.status === "success") {
                setSubmitSuccess(true)
                if (response.user?.token) {
                    localStorage.setItem(
                        "chaperon_auth_token",
                        response.user.token,
                    )
                }

                // Store user data in localStorage for quick access
                if (response.user) {
                    localStorage.setItem(
                        "chaperon_user_data",
                        JSON.stringify({
                            id: response.user.id,
                            email: response.user.email,
                            username: response.user.username,
                        }),
                    )
                }

                // Notify other components about auth state change
                window.dispatchEvent(new Event("authChange"))

                // If the user arrived via an invitation link, auto-accept it
                let invitationAccepted = false
                if (invitationToken && response.user?.id) {
                    try {
                        await acceptInvitation({
                            invitation_token: invitationToken,
                            user_id: response.user.id,
                        })
                        invitationAccepted = true
                    } catch (inviteErr) {
                        // Non-fatal - log and continue to dashboard
                        // TODO: Consider showing a notification about the failed auto-accept and how to resolve it
                        console.error(
                            "Failed to auto-accept invitation after registration:",
                            inviteErr,
                        )
                    }
                }

                const welcomeMessage =
                    invitationAccepted && invitationCommunityName
                        ? `Welcome to Chaperon! You've joined ${invitationCommunityName}.`
                        : "Registration successful! Welcome to Chaperon."

                setTimeout(() => {
                    navigate(invitationToken ? "/community" : from, {
                        state: {
                            message: welcomeMessage,
                            user: response.user,
                        },
                    })
                }, 2000)
            }
        } catch (error) {
            console.error("Registration error:", error)

            if (error.response?.data?.message) {
                const message = error.response.data.message

                if (message.includes("email") || message.includes("Email")) {
                    setErrors({ email: message })
                } else if (
                    message.includes("username") ||
                    message.includes("Username")
                ) {
                    setErrors({ username: message })
                } else if (
                    message.includes("password") ||
                    message.includes("Password")
                ) {
                    setErrors({ password: message })
                } else {
                    setErrors({ general: message })
                }
            } else {
                setErrors({
                    general: "An unexpected error occurred. Please try again.",
                })
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    if (submitSuccess) {
        return (
            <div className="auth-success-container">
                <Helmet>
                    <title>Registration Successful - Chaperon</title>
                </Helmet>

                <div className="auth-success-card">
                    <div className="mb-4 success-animation">
                        <i
                            className="bi bi-check-circle-fill text-success"
                            style={{ fontSize: "4rem" }}
                        ></i>
                    </div>
                    <h2 className="mb-3">Welcome to Chaperon!</h2>
                    <p className="mb-4">
                        Your account has been created successfully. You're being
                        redirected to your dashboard...
                    </p>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="auth-container">
            <Helmet>
                <title>Create Your Account - Chaperon</title>
                <meta
                    name="description"
                    content="Join Chaperon to access real-time safety data, insights, and alerts for destinations worldwide."
                />
            </Helmet>

            {/* Left Side - Brand/Image Section */}
            <div
                className="auth-image-section"
                style={{ backgroundImage: "url(/image/zen-lady-mountain.png)" }}
            >
                <div className="auth-brand-content">
                    <h1>Travel with Confidence</h1>
                    <p>
                        Join other travelers who trust Chaperon for
                        comprehensive safety insights and real-time alerts
                        worldwide.
                    </p>
                </div>
            </div>

            {/* Right Side - Registration Form */}
            <div className="auth-form-section">
                <div className="auth-form-container">
                    <div className="auth-form-header">
                        <h1>Create Your Account</h1>
                        <p>
                            Join Chaperon to access comprehensive safety
                            insights for your travels
                        </p>
                    </div>

                    {errors.general && (
                        <div className="alert alert-danger" role="alert">
                            {errors.general}
                        </div>
                    )}

                    <form
                        onSubmit={handleSubmit}
                        noValidate
                        className="auth-form"
                    >
                        <div className="row g-3">
                            {/* Email */}
                            <div className="col-12">
                                <input
                                    type="email"
                                    className={`form-control ${
                                        errors.email ? "is-invalid" : ""
                                    }`}
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Email Address *"
                                    required
                                />
                                {errors.email && (
                                    <div className="invalid-feedback">
                                        {errors.email}
                                    </div>
                                )}
                            </div>

                            {/* Username */}
                            <div className="col-12">
                                <input
                                    type="text"
                                    className={`form-control ${
                                        errors.username ? "is-invalid" : ""
                                    }`}
                                    id="username"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleInputChange}
                                    placeholder="Username *"
                                    required
                                />
                                {errors.username && (
                                    <div className="invalid-feedback">
                                        {errors.username}
                                    </div>
                                )}
                                <div className="form-text">
                                    3-50 characters, letters, numbers, and
                                    underscores only
                                </div>
                            </div>

                            {/* Password */}
                            <div className="col-12">
                                <input
                                    type="password"
                                    className={`form-control ${
                                        errors.password ? "is-invalid" : ""
                                    }`}
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    placeholder="Password *"
                                    required
                                />
                                {errors.password && (
                                    <div className="invalid-feedback">
                                        {errors.password}
                                    </div>
                                )}
                                <div className="form-text">
                                    Minimum 8 characters
                                </div>
                            </div>

                            {/* Confirm Password */}
                            <div className="col-12">
                                <input
                                    type="password"
                                    className={`form-control ${
                                        errors.confirmPassword
                                            ? "is-invalid"
                                            : ""
                                    }`}
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleInputChange}
                                    placeholder="Confirm Password *"
                                    required
                                />
                                {errors.confirmPassword && (
                                    <div className="invalid-feedback">
                                        {errors.confirmPassword}
                                    </div>
                                )}
                            </div>

                            {/* Terms and Privacy */}
                            <div className="col-12">
                                <div className="form-text text-center">
                                    By creating an account, you agree to our{" "}
                                    <Link to="/terms" className="auth-link">
                                        Terms of Use
                                    </Link>{" "}
                                    and{" "}
                                    <Link to="/privacy" className="auth-link">
                                        Privacy Policy
                                    </Link>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="col-12">
                                <button
                                    type="submit"
                                    className="btn-auth-primary"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                            ></span>
                                            Creating Account...
                                        </>
                                    ) : (
                                        "Create Account"
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>

                    <div className="auth-divider">
                        <span>Already have an account?</span>
                    </div>

                    <div className="text-center">
                        <Link
                            to="/user/login"
                            state={location.state}
                            className="auth-link"
                        >
                            Sign in here
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    )
}
